/**
 * src/data/GauntletBank.js
 * Hardcoded scenario bank for Final Exam: The Gauntlet.
 */
export const gauntletBank = [
  {
    "id": "q1",
    "survivalRate": 0.45,
    "text": "Your direct manager publicly takes credit for a massive data model you built from scratch during an all-hands meeting. What is your immediate play?",
    "options": [
      { "id": "a", "text": "Stay completely silent and vent to your coworkers later to blow off steam.", "correct": false },
      { "id": "b", "text": "Interrupt them live on the call to clarify that you actually built the model.", "correct": false },
      { "id": "c", "text": "Send a polite email thanking them for showcasing the team's work, hoping they acknowledge you next time.", "correct": false },
      { "id": "d", "text": "Protect their optics publicly, but confront them privately 1-on-1 later to establish firm credit boundaries.", "correct": true }
    ]
  },
  {
    "id": "q2",
    "survivalRate": 0.32,
    "text": "A key enterprise client emails you furiously at 4:55 PM on Friday, demanding a root-cause analysis for a service outage that wasn't your team's fault.",
    "options": [
      { "id": "a", "text": "Ignore the email until Monday morning to protect your work-life boundaries.", "correct": false },
      { "id": "b", "text": "Reply defensively, throwing the engineering team under the bus and telling the client they have the wrong department.", "correct": false },
      { "id": "c", "text": "Send a polite corporate template apologizing for their frustration and hope they calm down over the weekend.", "correct": false },
      { "id": "d", "text": "Acknowledge receipt immediately, assure them cross-functional triage is underway, and commit to an ETA for the formal RCA by Monday morning.", "correct": true }
    ]
  },
  {
    "id": "q3",
    "survivalRate": 0.18,
    "text": "You discover a critical security vulnerability in the legacy codebase 48 hours before your company's IPO lock-up period ends.",
    "options": [
      { "id": "a", "text": "Keep it quiet to ensure the IPO goes smoothly without panic, assuming the risk of a breach is statistically low.", "correct": false },
      { "id": "b", "text": "Blast the entire engineering org on Slack, demanding to know who wrote the vulnerable code and halting all deployments.", "correct": false },
      { "id": "c", "text": "Log a standard low-priority Jira ticket so it gets addressed in the next sprint after the IPO dust settles.", "correct": false },
      { "id": "d", "text": "Immediately escalate to the CISO via a secure, private channel with a proposed hotfix timeline and blast-radius assessment.", "correct": true }
    ]
  },
  {
    "id": "q4",
    "survivalRate": 0.28,
    "text": "A lateral peer repeatedly drops the ball on their deliverables, bottlenecking your project. They tell you privately they are overwhelmed with personal issues.",
    "options": [
      { "id": "a", "text": "Do their work for them in secret so the project doesn't fail and they don't get in trouble.", "correct": false },
      { "id": "b", "text": "CC their manager on the next missed deliverable to force accountability and protect your own reputation.", "correct": false },
      { "id": "c", "text": "Tell them 'take all the time you need, mental health is a priority' while letting your project silently miss its deadline.", "correct": false },
      { "id": "d", "text": "Express empathy, but immediately realign with your shared manager on resourcing and deadline adjustments to protect both the peer and the project.", "correct": true }
    ]
  },
  {
    "id": "q5",
    "survivalRate": 0.55,
    "text": "During a board meeting, the CEO asks you for a specific financial projection that you haven't calculated yet.",
    "options": [
      { "id": "a", "text": "Freeze, apologize profusely, and state you are completely unprepared for the question.", "correct": false },
      { "id": "b", "text": "Make up a plausible-sounding number to project confidence and figure out how to justify it later.", "correct": false },
      { "id": "c", "text": "Give a 10-minute long-winded explanation of your methodology to stall for time and sound smart.", "correct": false },
      { "id": "d", "text": "State confidently: 'I don't have the exact figure, but I will run the model and deliver the projection to you by EOD.'", "correct": true }
    ]
  },
  {
    "id": "q6",
    "survivalRate": 0.24,
    "text": "A senior executive bypasses your manager and asks you directly to drop your current sprint to build a pet project for them.",
    "options": [
      { "id": "a", "text": "Say yes immediately to gain political favor, secretly working 80 hours to do both projects and burning out.", "correct": false },
      { "id": "b", "text": "Refuse aggressively, telling them they must respect the chain of command and talk to your boss.", "correct": false },
      { "id": "c", "text": "Politely say you will add it to the backlog and hope they forget about it.", "correct": false },
      { "id": "d", "text": "Reply: 'Happy to help. Let me quickly align with my manager to reprioritize my current deliverables to accommodate this new scope.'", "correct": true }
    ]
  },
  {
    "id": "q7",
    "survivalRate": 0.41,
    "text": "You accidentally send a highly confidential internal pricing sheet to an external third-party vendor instead of internal procurement.",
    "options": [
      { "id": "a", "text": "Recall the email in Outlook and pretend it never happened, hoping they didn't see it.", "correct": false },
      { "id": "b", "text": "Send a furious email demanding the vendor delete it immediately or face catastrophic legal action.", "correct": false },
      { "id": "c", "text": "Send a polite follow-up saying 'Please disregard the previous email, it was sent in error.'", "correct": false },
      { "id": "d", "text": "Call the vendor immediately to explain the error, request deletion, and self-report the breach to your compliance officer.", "correct": true }
    ]
  },
  {
    "id": "q8",
    "survivalRate": 0.35,
    "text": "Your newly assigned direct report is highly talented but constantly toxic in team meetings, rolling their eyes at peers' suggestions.",
    "options": [
      { "id": "a", "text": "Avoid interacting with them in meetings to prevent further conflict and let them operate in a silo.", "correct": false },
      { "id": "b", "text": "Call them out publicly during the next meeting to assert your dominance and humiliate them into compliance.", "correct": false },
      { "id": "c", "text": "Send the whole team an HR-approved article on 'active listening' and hope the toxic employee takes the hint.", "correct": false },
      { "id": "d", "text": "Pull them into a private 1-on-1, explicitly detail the observed behavior, and mandate a behavioral shift as a condition of their ongoing employment.", "correct": true }
    ]
  },
  {
    "id": "q9",
    "survivalRate": 0.62,
    "text": "You inherit an offshore contractor team that is 4 weeks behind schedule. The previous manager blamed them for being 'lazy.'",
    "options": [
      { "id": "a", "text": "Accept the previous manager's assessment and passively let the project fail to prove it wasn't your fault.", "correct": false },
      { "id": "b", "text": "Fire the entire offshore team immediately to set an example and demand new contractors.", "correct": false },
      { "id": "c", "text": "Send a highly polite email asking them to please work faster to hit the new targets, offering virtual high-fives.", "correct": false },
      { "id": "d", "text": "Halt development, audit their operating procedures, and discover/fix the broken API documentation that is actually blocking their work.", "correct": true }
    ]
  },
  {
    "id": "q10",
    "survivalRate": 0.22,
    "text": "You overhear two VPs casually discussing an upcoming 20% workforce reduction that hasn't been announced.",
    "options": [
      { "id": "a", "text": "Drop anonymous hints in the company suggestion box that morale might drop soon, hoping HR does something.", "correct": false },
      { "id": "b", "text": "Confront the VPs in the hallway and demand to know if your job is safe.", "correct": false },
      { "id": "c", "text": "Schedule a skip-level to politely ask if there is anything you can do to help the company during 'turbulent times'.", "correct": false },
      { "id": "d", "text": "Keep strict confidentiality, compartmentalize the information, and ensure your team's current deliverables remain flawless.", "correct": true }
    ]
  },
  {
    "id": "q11",
    "survivalRate": 0.48,
    "text": "A major product launch goes live with a critical bug. The marketing team blames your engineering team in a public Slack channel.",
    "options": [
      { "id": "a", "text": "Log off Slack for the rest of the day to avoid the toxicity and let someone else handle the fallout.", "correct": false },
      { "id": "b", "text": "Reply in the channel blaming the QA team for missing it and telling Marketing to stay in their lane.", "correct": false },
      { "id": "c", "text": "Reply with a corporate apology template, validate their feelings, and lock the channel.", "correct": false },
      { "id": "d", "text": "Move the conversation to a dedicated incident channel, roll back the deployment, and schedule a blameless post-mortem.", "correct": true }
    ]
  },
  {
    "id": "q12",
    "survivalRate": 0.38,
    "text": "You are leading a cross-functional project and a key stakeholder continually skips the weekly sync, causing cascading delays.",
    "options": [
      { "id": "a", "text": "Continue running the project blindly without their input and let it fail.", "correct": false },
      { "id": "b", "text": "Escalate to the CEO immediately to get them formally reprimanded for insubordination.", "correct": false },
      { "id": "c", "text": "Continue sending polite recap emails hoping they eventually read them and catch up.", "correct": false },
      { "id": "d", "text": "Schedule a 15-minute private sync, identify their specific bandwidth constraints, and redefine their necessary contribution model.", "correct": true }
    ]
  },
  {
    "id": "q13",
    "survivalRate": 0.25,
    "text": "Your primary supplier announces a 30% price hike effective in 48 hours, which will destroy your quarterly margin.",
    "options": [
      { "id": "a", "text": "Accept the price hike passively and hope Q4 revenue magically makes up for the margin loss.", "correct": false },
      { "id": "b", "text": "Threaten the supplier with legal action for breach of trust and instantly terminate the contract with no backup plan.", "correct": false },
      { "id": "c", "text": "Politely ask them to reconsider and offer to write them a good review on industry forums if they keep the old price.", "correct": false },
      { "id": "d", "text": "Model the financial impact immediately, brief leadership with mitigation options, and open aggressive renegotiations for volume discounts.", "correct": true }
    ]
  },
  {
    "id": "q14",
    "survivalRate": 0.54,
    "text": "You are asked to approve an expense report for a top-performing salesperson that clearly includes $500 of unapproved personal leisure expenses.",
    "options": [
      { "id": "a", "text": "Approve it quietly because they are a top earner and you don't want to upset them or hurt their morale.", "correct": false },
      { "id": "b", "text": "Reject it and publicly shame them in the sales Slack channel for attempted expense fraud.", "correct": false },
      { "id": "c", "text": "Approve it but leave a polite comment saying 'Please don't do this again next time, accounting might get mad.'", "correct": false },
      { "id": "d", "text": "Reject the specific line items, approve the legitimate ones, and privately remind them of strict T&E compliance limits.", "correct": true }
    ]
  },
  {
    "id": "q15",
    "survivalRate": 0.17,
    "text": "An algorithm your team deployed begins showing clear, documented demographic bias in its loan approval outputs.",
    "options": [
      { "id": "a", "text": "Ignore the data; it was trained on historical sets and isn't your personal legal responsibility.", "correct": false },
      { "id": "b", "text": "Immediately shut down the entire product line without executive approval, costing millions in revenue instantly.", "correct": false },
      { "id": "c", "text": "Add a polite disclaimer to the website saying the tool is still in beta and may have anomalies.", "correct": false },
      { "id": "d", "text": "Initiate a controlled failover to the legacy system, alert legal/compliance, and patch the model's weighting parameters.", "correct": true }
    ]
  },
  {
    "id": "q16",
    "survivalRate": 0.61,
    "text": "A junior employee on your team shares an incredibly innovative workflow solution during a brainstorming session, but stumbles over their words nervously.",
    "options": [
      { "id": "a", "text": "Let the awkward silence linger until someone else changes the subject and the idea is lost.", "correct": false },
      { "id": "b", "text": "Cut them off and re-explain their idea loudly so you look like the visionary leader to the executives in the room.", "correct": false },
      { "id": "c", "text": "Smile politely and say 'That's a nice thought,' then pivot back to your own agenda entirely.", "correct": false },
      { "id": "d", "text": "Validate the idea publicly, synthesize it for the room, and assign them as the lead to execute a proof-of-concept.", "correct": true }
    ]
  },
  {
    "id": "q17",
    "survivalRate": 0.33,
    "text": "You realize you completely forgot to file a mandatory compliance report that was due to a federal regulator yesterday.",
    "options": [
      { "id": "a", "text": "Backdate the PDF signature and file it quietly, hoping no one audits the timestamp.", "correct": false },
      { "id": "b", "text": "Blame your administrative assistant publicly for not putting it on your calendar.", "correct": false },
      { "id": "c", "text": "File it today with a polite note to the regulator saying 'Sorry I was busy with meetings, hope you understand!'", "correct": false },
      { "id": "d", "text": "File it immediately, inform your internal compliance officer of the exact delay, and implement a systemic calendar failsafe.", "correct": true }
    ]
  },
  {
    "id": "q18",
    "survivalRate": 0.42,
    "text": "A critical software vendor's cloud platform goes down for 24 hours. They offer you free VIP concert tickets as an apology right before contract renewal.",
    "options": [
      { "id": "a", "text": "Decline professionally, citing strict corporate anti-bribery policies, and proceed with a completely objective vendor evaluation.", "correct": true },
      { "id": "b", "text": "Accuse the vendor of bribery, cancel the contract immediately, and threaten to expose them on LinkedIn.", "correct": false },
      { "id": "c", "text": "Politely accept the tickets but donate them to charity so you don't personally benefit, avoiding an awkward conversation.", "correct": false },
      { "id": "d", "text": "Accept the tickets quietly, rationalizing that it won't actually affect your objective decision on the contract.", "correct": false }
    ]
  },
  {
    "id": "q19",
    "survivalRate": 0.28,
    "text": "You uncover a critical flaw in your product that just shipped to 5,000 enterprise customers. It will cost $2M to patch, but might go unnoticed for months.",
    "options": [
      { "id": "a", "text": "Keep it quiet and assume customer support can just handle the few tickets that might trickle in over time.", "correct": false },
      { "id": "b", "text": "Brief the executive team immediately with a cost-benefit analysis and a proactive, phased patch deployment plan to contain the blast radius.", "correct": true },
      { "id": "c", "text": "Write a polite FAQ page addressing 'potential minor bugs' to vaguely cover your bases legally.", "correct": false },
      { "id": "d", "text": "Instantly trigger a massive public recall without consulting the C-suite, causing a global PR nightmare.", "correct": false }
    ]
  },
  {
    "id": "q20",
    "survivalRate": 0.65,
    "text": "Your most reliable employee starts submitting sloppy work riddled with errors three days in a row right before a massive launch.",
    "options": [
      { "id": "a", "text": "Put them on a formal Performance Improvement Plan (PIP) immediately to establish a ruthless paper trail.", "correct": false },
      { "id": "b", "text": "Send them a polite Slack message with a smiley face saying 'Hey, let's try to be a bit more careful!'", "correct": false },
      { "id": "c", "text": "Schedule a private 1-on-1, highlight the sudden drop in quality, and ask with genuine empathy if they are experiencing burnout or personal blockers.", "correct": true },
      { "id": "d", "text": "Silently fix their errors yourself late at night so you don't embarrass them or slow down the launch.", "correct": false }
    ]
  },
  {
    "id": "q21",
    "survivalRate": 0.33,
    "text": "An internal IT audit reveals half your department is using an unauthorized, unsecure Generative AI tool to write client contracts, violating InfoSec policy.",
    "options": [
      { "id": "a", "text": "Ignore the report since the AI tool is saving everyone 10 hours a week and boosting your KPI metrics.", "correct": false },
      { "id": "b", "text": "Temporarily block the unauthorized tool at the network level, while rapidly partnering with IT to procure an enterprise-grade, secure AI alternative.", "correct": true },
      { "id": "c", "text": "Fire the worst offenders on the spot to set a zero-tolerance example across the enterprise.", "correct": false },
      { "id": "d", "text": "Send a polite email reminding everyone to 'please review the employee handbook regarding software usage.'", "correct": false }
    ]
  },
  {
    "id": "q22",
    "survivalRate": 0.19,
    "text": "The CEO announces a strict 5-day Return-To-Office mandate. Half your highly productive engineering team threatens to resign immediately.",
    "options": [
      { "id": "a", "text": "Host a 'listening session' where you validate their feelings but offer zero actionable compromises.", "correct": false },
      { "id": "b", "text": "Do nothing, strictly enforce the mandate, and passively watch your top talent walk out the door.", "correct": false },
      { "id": "c", "text": "Tell them they are easily replaceable and to hand in their laptops if they don't like the new rules.", "correct": false },
      { "id": "d", "text": "Synthesize retention risk data, enforce baseline policies, and aggressively petition the executive team with productivity metrics to negotiate a hybrid carve-out.", "correct": true }
    ]
  },
  {
    "id": "q23",
    "survivalRate": 0.73,
    "text": "During a high-stakes pitch, a potential enterprise client asks a deeply technical question you do not know the answer to, and your lead engineer is out sick.",
    "options": [
      { "id": "a", "text": "Smile politely and give a vague, buzzword-heavy answer about 'scalable cloud synergies.'", "correct": false },
      { "id": "b", "text": "Tell them they don't need to know that level of detail yet and should just trust your product's architecture.", "correct": false },
      { "id": "c", "text": "Try to change the subject rapidly and hope they don't notice you entirely avoided the question.", "correct": false },
      { "id": "d", "text": "Acknowledge the complexity, state plainly that you need to verify with your engineering lead, and commit to a precise follow-up answer by EOD.", "correct": true }
    ]
  },
  {
    "id": "q24",
    "survivalRate": 0.47,
    "text": "Your direct manager orders you to doctor a Q3 performance report to make the departmental metrics look 15% higher before sending it to the CEO.",
    "options": [
      { "id": "a", "text": "Do it quietly to protect your job, telling yourself you were just following orders.", "correct": false },
      { "id": "b", "text": "Refuse the illegal directive in writing, submit the accurate data, and forward the paper trail to Legal/Compliance.", "correct": true },
      { "id": "c", "text": "Politely tell your boss, 'I'm not super comfortable with this, could you ask someone else to do it?'", "correct": false },
      { "id": "d", "text": "CC the entire company on an email exposing your boss as a fraud and a liar.", "correct": false }
    ]
  },
  {
    "id": "q25",
    "survivalRate": 0.58,
    "text": "A competitor unexpectedly launches an AI feature that completely makes your team's upcoming Q3 product roadmap obsolete.",
    "options": [
      { "id": "a", "text": "Halt the current sprints, conduct a rapid gap analysis, and pivot development resources toward your platform's remaining unique value proposition.", "correct": true },
      { "id": "b", "text": "Host a mindfulness session to help the team process their disappointment, but take no immediate strategic action.", "correct": false },
      { "id": "c", "text": "Scrap the roadmap instantly and force your team to work 100-hour weeks to clone the competitor's feature in one month.", "correct": false },
      { "id": "d", "text": "Continue building your outdated roadmap anyway, hoping marketing can spin it as 'more secure' than the competitor.", "correct": false }
    ]
  },
  {
    "id": "q26",
    "survivalRate": 0.38,
    "text": "A highly respected senior executive routinely makes culturally inappropriate jokes during leadership meetings, making lateral peers visibly uncomfortable.",
    "options": [
      { "id": "a", "text": "Slam your fists on the boardroom table and scream at them for being a toxic dinosaur.", "correct": false },
      { "id": "b", "text": "Address the behavior privately with the executive using explicit examples, and if uncorrected, escalate a documented report to HR/Legal.", "correct": true },
      { "id": "c", "text": "Laugh along to fit in, protecting your own career trajectory and political capital.", "correct": false },
      { "id": "d", "text": "Vaguely mention to HR that 'some people are bothered by the vibe' but refuse to name the specific executive.", "correct": false }
    ]
  },
  {
    "id": "q27",
    "survivalRate": 0.44,
    "text": "A cross-functional leader constantly bypasses you to steal your team's top engineers for their own side-projects without asking permission.",
    "options": [
      { "id": "a", "text": "Send them a polite Slack message asking if they could 'pretty please keep me in the loop next time.'", "correct": false },
      { "id": "b", "text": "Confront the leader in the hallway and scream at them for poaching your talent and ruining your sprint.", "correct": false },
      { "id": "c", "text": "Escalate to a formal resource-allocation sync with leadership, establishing strict project gating procedures to protect your team's operational bandwidth.", "correct": true },
      { "id": "d", "text": "Let them take the engineers so you don't seem uncooperative, even as your own metrics suffer.", "correct": false }
    ]
  },
  {
    "id": "q28",
    "survivalRate": 0.16,
    "text": "You are forced to lay off 10% of your department. During a 1-on-1 termination video call, an employee starts crying uncontrollably.",
    "options": [
      { "id": "a", "text": "Remain present, hold space for their reaction with compassionate silence, and clearly guide them through their severance and transition resources.", "correct": true },
      { "id": "b", "text": "Mute your microphone, look away from the camera, and wait for HR to handle all the emotional labor.", "correct": false },
      { "id": "c", "text": "Tell them to act professional and remind them that the company's stock price is struggling too.", "correct": false },
      { "id": "d", "text": "Over-promise that you'll do everything in your power to hire them back as soon as budgets open up, even though you can't guarantee it.", "correct": false }
    ]
  },
  {
    "id": "q29",
    "survivalRate": 0.51,
    "text": "A critical compliance training deadline is in 2 hours. Your top engineer refuses to take it, saying they are 'too busy coding.'",
    "options": [
      { "id": "a", "text": "Take the training module for them so they can keep coding and the company stays compliant.", "correct": false },
      { "id": "b", "text": "Physically or virtually intercept them, explain the absolute regulatory necessity, and mandate they stop coding for 20 minutes to complete it.", "correct": true },
      { "id": "c", "text": "Revoke all their system accesses immediately and threaten to fire them for insubordination.", "correct": false },
      { "id": "d", "text": "Let the deadline pass for them, hoping the compliance team won't notice one missing certificate.", "correct": false }
    ]
  },
  {
    "id": "q30",
    "survivalRate": 0.59,
    "text": "You discover a spreadsheet proving a massive gender pay gap on your team that predates your recent promotion to Director.",
    "options": [
      { "id": "a", "text": "Close the file and pretend you never saw it so you don't inherit the political headache of fixing it.", "correct": false },
      { "id": "b", "text": "Send an email to the women on the team thanking them for their hard work and promising a vague 'culture shift' next year.", "correct": false },
      { "id": "c", "text": "Partner with HR and Finance immediately to draft a systemic, budgeted equity adjustment plan for the next formal compensation cycle.", "correct": true },
      { "id": "d", "text": "Instantly auto-adjust everyone's salary in the payroll system without telling HR to forcefully balance the scales.", "correct": false }
    ]
  },
  {
    "id": "q31",
    "survivalRate": 0.22,
    "text": "You discover that a critical software license expired yesterday, bringing a multi-million dollar client's operations to a complete halt.",
    "options": [
      { "id": "a", "text": "Purchase a temporary emergency license on your corporate card to instantly restore client uptime, then deal with the internal procurement red tape retroactively.", "correct": true },
      { "id": "b", "text": "Send the client a polite, beautifully formatted apology letter explaining the intricacies of your company's procurement software.", "correct": false },
      { "id": "c", "text": "Submit a standard IT ticket and wait the mandatory 48 hours for procurement to process the renewal while the client suffers.", "correct": false },
      { "id": "d", "text": "Scream at the IT director on a public Zoom call for dropping the ball on the renewal tracking.", "correct": false }
    ]
  },
  {
    "id": "q32",
    "survivalRate": 0.61,
    "text": "You find out your manager is resigning in two weeks. They tell you confidentially and ask you not to tell the team yet.",
    "options": [
      { "id": "a", "text": "Throw a surprise goodbye party the next day, assuming they meant 'don't tell them in a formal meeting.'", "correct": false },
      { "id": "b", "text": "Panic internally and start aggressively applying for jobs, assuming the department is going to collapse.", "correct": false },
      { "id": "c", "text": "Leak the news to the team immediately so you look like the insider who has all the information.", "correct": false },
      { "id": "d", "text": "Respect the confidentiality, but immediately begin quietly documenting critical transition workflows to ensure team stability.", "correct": true }
    ]
  },
  {
    "id": "q33",
    "survivalRate": 0.17,
    "text": "A highly sensitive, unreleased quarterly earnings report leaks to the press. You discover undeniable proof that your best friend at the company leaked it.",
    "options": [
      { "id": "a", "text": "Delete the proof and stay completely silent to protect your friend, assuming the SEC won't investigate.", "correct": false },
      { "id": "b", "text": "Escalate the objective facts to Legal and Compliance immediately; corporate fiduciary duty explicitly supersedes personal workplace friendships.", "correct": true },
      { "id": "c", "text": "Reply-all to the executive team with screenshots, publicly destroying your friend to prove your own loyalty.", "correct": false },
      { "id": "d", "text": "Tell your friend to confess to HR 'because it's the right thing to do,' but take no further action yourself.", "correct": false }
    ]
  },
  {
    "id": "q34",
    "survivalRate": 0.49,
    "text": "You inherit a team after a merger. The top performer from the acquired company refuses to use your company's mandated project management software, claiming it slows them down.",
    "options": [
      { "id": "a", "text": "Let them use their old software so you don't upset them during a sensitive transition period.", "correct": false },
      { "id": "b", "text": "Terminate them immediately for insubordination to assert your dominance over the newly acquired team.", "correct": false },
      { "id": "c", "text": "Send a polite email linking to the IT handbook, hoping they eventually read it and change their behavior.", "correct": false },
      { "id": "d", "text": "Schedule a 1-on-1, explicitly enforce the new operational standard, but actively listen to their UX feedback to improve the new workflow.", "correct": true }
    ]
  },
  {
    "id": "q35",
    "survivalRate": 0.24,
    "text": "A key team member has started crying repeatedly in 1-on-1s due to severe personal life issues, and their deliverables are failing, bottlenecking the sprint.",
    "options": [
      { "id": "a", "text": "Ignore the emotional outbursts entirely and quietly do their work for them so the sprint doesn't fail.", "correct": false },
      { "id": "b", "text": "Tell them firmly that they need to leave their personal baggage at home because the business cannot afford to wait.", "correct": false },
      { "id": "c", "text": "Send them an email with a link to the company's mental health portal and a smiley face, hoping they feel better soon.", "correct": false },
      { "id": "d", "text": "Mandate they take immediate paid time off to stabilize, formally connect them with EAP, and realign the team's sprint capacity without them.", "correct": true }
    ]
  },
  {
    "id": "q36",
    "survivalRate": 0.31,
    "text": "You discover undeniable proof that your company's finance team has been accidentally overbilling a massive enterprise client by 15% for the last six months.",
    "options": [
      { "id": "a", "text": "Close the spreadsheet and pretend you never saw it; it's Finance's problem, not yours.", "correct": false },
      { "id": "b", "text": "Email the client directly with the proof to show you are honest, throwing your Finance team under the bus.", "correct": false },
      { "id": "c", "text": "Send a polite Slack message to a junior accountant asking them if they could 'maybe double-check the billing math when they have a second.'", "correct": false },
      { "id": "d", "text": "Escalate immediately to the CFO with the documented proof and a proposed proactive refund strategy to preserve the client relationship.", "correct": true }
    ]
  },
  {
    "id": "q37",
    "survivalRate": 0.18,
    "text": "The VP of Sales promises a massive new client a complex software feature that doesn't exist, signing a $5M contract with a 30-day delivery deadline.",
    "options": [
      { "id": "a", "text": "Say nothing, force your engineering team to work 100-hour weeks, and pray you can duct-tape a prototype together.", "correct": false },
      { "id": "b", "text": "Cancel the contract immediately and publicly scream at the VP of Sales in the executive sync for lying to clients.", "correct": false },
      { "id": "c", "text": "Send a highly polite apology email to the client stating 'we will add this to our Q4 roadmap' and hope they don't cancel.", "correct": false },
      { "id": "d", "text": "Partner with Sales leadership to immediately confess the gap to the client, while presenting a viable interim workaround and a realistic, accelerated delivery timeline.", "correct": true }
    ]
  },
  {
    "id": "q38",
    "survivalRate": 0.41,
    "text": "You notice your Director of Procurement frequently accepting lavish, unrecorded VIP sports tickets from a vendor who always wins your company's RFPs.",
    "options": [
      { "id": "a", "text": "Keep your head down and stay out of it. You don't have hard financial proof and don't want to get fired.", "correct": false },
      { "id": "b", "text": "Confront the Director in the middle of the open-plan office, accusing them of bribery and corruption.", "correct": false },
      { "id": "c", "text": "Send a polite company-wide reminder email about the corporate gift policy, hoping the Director takes the hint.", "correct": false },
      { "id": "d", "text": "Quietly compile the circumstantial timeline of gifts and contract awards, and escalate it strictly to the General Counsel or anonymous ethics hotline.", "correct": true }
    ]
  },
  {
    "id": "q39",
    "survivalRate": 0.52,
    "text": "Your boss asks you to present their new operational strategy to the C-suite tomorrow. You review it and realize the strategy has a fatal mathematical flaw and will fail.",
    "options": [
      { "id": "a", "text": "Present the strategy exactly as written to avoid angering your boss, letting the C-suite discover the flaw later.", "correct": false },
      { "id": "b", "text": "Publicly humiliate your boss in front of the C-suite by pointing out the math flaw and pitching your own superior idea.", "correct": false },
      { "id": "c", "text": "Add a polite disclaimer slide at the beginning saying 'There are some potential risks,' but offer no actual solutions.", "correct": false },
      { "id": "d", "text": "Pre-wire with your boss immediately, highlight the fatal flaw privately, and co-create a pivot so you present a unified, competent front to the C-suite.", "correct": true }
    ]
  },
  {
    "id": "q40",
    "survivalRate": 0.29,
    "text": "During a video call, a high-value enterprise client begins verbally abusing and swearing at a junior account manager on your team over a minor delay.",
    "options": [
      { "id": "a", "text": "Stay on mute. The client brings in $2M a year, so the junior employee just needs to develop a thicker skin.", "correct": false },
      { "id": "b", "text": "Unmute, curse out the client for being a bully, and instantly terminate the call and their contract.", "correct": false },
      { "id": "c", "text": "Send the client a polite follow-up email after the call asking for 'more constructive and respectful dialogue moving forward.'", "correct": false },
      { "id": "d", "text": "Intervene live, firmly establish professional boundaries, de-escalate the tone, and take over the direct account management from the junior employee.", "correct": true }
    ]
  },
  {
    "id": "q41",
    "survivalRate": 0.61,
    "text": "The CFO abruptly cuts your departmental budget by 40%, but the CEO emails you expecting the exact same Q4 output and deliverables.",
    "options": [
      { "id": "a", "text": "Reply 'We will make it happen!' and let your team burn out working weekends until they all quit.", "correct": false },
      { "id": "b", "text": "Reply 'This is impossible, I refuse to do any work until my budget is restored.'", "correct": false },
      { "id": "c", "text": "Send a memo to your team politely asking everyone to 'be a bit more efficient with their time this quarter.'", "correct": false },
      { "id": "d", "text": "Present the CEO with a prioritized matrix of what can actually be delivered under the new constraints, forcing a strategic executive trade-off decision.", "correct": true }
    ]
  },
  {
    "id": "q42",
    "survivalRate": 0.35,
    "text": "You find out a cross-functional peer took full credit for your team's successful launch during a global all-hands call, without mentioning you or your staff once.",
    "options": [
      { "id": "a", "text": "Let it go. Taking the high road is better than looking petty in front of the executives.", "correct": false },
      { "id": "b", "text": "Unmute yourself on the all-hands call and say 'Actually, my team did all the work, not you!'", "correct": false },
      { "id": "c", "text": "Send a polite email to the peer saying, 'Great presentation today! I hope you liked our team's work.'", "correct": false },
      { "id": "d", "text": "Post a follow-up in the global company channel thanking the peer for highlighting the project, explicitly tagging and praising the members of your team who built it.", "correct": true }
    ]
  },
  {
    "id": "q43",
    "survivalRate": 0.15,
    "text": "You discover a malicious backdoor hidden within a widely used open-source library that your core enterprise platform heavily relies upon.",
    "options": [
      { "id": "a", "text": "Wait passively for the library's official maintainers to push a security patch so you don't break the build.", "correct": false },
      { "id": "b", "text": "Instantly delete the library from your servers without warning, taking down the entire enterprise platform for all users.", "correct": false },
      { "id": "c", "text": "Put a polite banner on the user dashboard stating 'Our site may be experiencing security instability, please clear your cache.'", "correct": false },
      { "id": "d", "text": "Trigger a severity-1 incident, instantly roll back to the last known safe version, lock the dependency, and initiate an InfoSec blast-radius audit.", "correct": true }
    ]
  },
  {
    "id": "q44",
    "survivalRate": 0.48,
    "text": "A massive regional blackout knocks out power and internet for 80% of your remote team the day before a non-negotiable compliance deadline.",
    "options": [
      { "id": "a", "text": "Miss the deadline completely and just blame the weather when the regulatory fines hit.", "correct": false },
      { "id": "b", "text": "Demand the team drive through hazardous conditions to find coffee shops with generators to finish the work.", "correct": false },
      { "id": "c", "text": "Send a nice email saying 'Stay safe everyone!' but offer zero operational backup plan to the business.", "correct": false },
      { "id": "d", "text": "Communicate a force majeure delay to stakeholders immediately, prioritize employee safety, and implement a staggered, triage-based recovery timeline.", "correct": true }
    ]
  },
  {
    "id": "q45",
    "survivalRate": 0.55,
    "text": "A misconfigured HR platform accidentally makes everyone's salary, including executive compensation, visible to the entire company. You notice it first.",
    "options": [
      { "id": "a", "text": "Pretend you didn't see it and quickly close the window so you aren't implicated.", "correct": false },
      { "id": "b", "text": "Blast the HR team in the public company Slack channel for their sheer incompetence.", "correct": false },
      { "id": "c", "text": "Send a polite email to the general HR inbox saying 'I think there might be a glitch with the new system.'", "correct": false },
      { "id": "d", "text": "Immediately page the IT and HR leadership via emergency channels to pull the system offline, containing the data leak within minutes.", "correct": true }
    ]
  },
  {
    "id": "q46",
    "survivalRate": 0.42,
    "text": "A key stakeholder completely changes the scope of a 6-month project just 72 hours before the final launch deadline.",
    "options": [
      { "id": "a", "text": "Agree immediately, force the team to pull three all-nighters, and deliver a horribly buggy, untested product.", "correct": false },
      { "id": "b", "text": "Tell the stakeholder they are ridiculous and flat-out refuse to do any more work for them.", "correct": false },
      { "id": "c", "text": "Ask them politely if they are 'absolutely sure' they want to make these changes, hoping they change their mind.", "correct": false },
      { "id": "d", "text": "Baseline the current deliverable, halt the new scope, and require the stakeholder to sign off on a formal change order with a new budget and a 4-week timeline extension.", "correct": true }
    ]
  },
  {
    "id": "q47",
    "survivalRate": 0.38,
    "text": "During a recorded internal meeting, a senior VP makes a clearly racist remark disguised as a 'joke' toward a junior person of color on your team.",
    "options": [
      { "id": "a", "text": "Laugh nervously and ignore it so you don't ruin your own chances of promotion.", "correct": false },
      { "id": "b", "text": "Start screaming at the VP, quit on the spot, and post the Zoom recording on Twitter.", "correct": false },
      { "id": "c", "text": "Send an email later to the junior employee saying 'I'm so sorry that happened, he's just from a different generation.'", "correct": false },
      { "id": "d", "text": "Halt the meeting, explicitly state the remark violates company values, check in with the impacted employee, and escalate the recording directly to HR.", "correct": true }
    ]
  },
  {
    "id": "q48",
    "survivalRate": 0.67,
    "text": "You realize the Q4 sales targets handed down by the board of directors are mathematically impossible based on your current CRM pipeline data.",
    "options": [
      { "id": "a", "text": "Keep quiet, push the team fruitlessly, and just accept the failure when Q4 ends.", "correct": false },
      { "id": "b", "text": "Send a fiery email to the board accusing them of being delusional and disconnected from reality.", "correct": false },
      { "id": "c", "text": "Send a motivational email to the team saying 'These goals are tough, but if we believe in ourselves, anything is possible!'", "correct": false },
      { "id": "d", "text": "Build a data-backed pipeline forecast model, present the reality to the executive team, and pivot the strategy toward securing high-margin, retainable accounts.", "correct": true }
    ]
  },
  {
    "id": "q49",
    "survivalRate": 0.22,
    "text": "You accidentally overhear your CEO negotiating to sell the company. If it goes through, you know your entire department will be made redundant.",
    "options": [
      { "id": "a", "text": "Start quietly slacking off, doing the bare minimum until you get laid off and collect severance.", "correct": false },
      { "id": "b", "text": "Storm into the CEO's office and demand a massive retention bonus in exchange for your silence.", "correct": false },
      { "id": "c", "text": "Host a polite team meeting encouraging everyone to 'focus on personal development' in case of any future organizational changes.", "correct": false },
      { "id": "d", "text": "Maintain absolute confidentiality, execute your duties flawlessly to maximize enterprise valuation, and discreetly begin networking for your next role.", "correct": true }
    ]
  },
  {
    "id": "q50",
    "survivalRate": 0.76,
    "text": "A new hire makes a devastating configuration error that deletes a week of work. They immediately confess to you, terrified they will be fired.",
    "options": [
      { "id": "a", "text": "Pretend you didn't hear them and let them try to fix it silently so you aren't implicated in the failure.", "correct": false },
      { "id": "b", "text": "Fire them instantly to set a precedent that mistakes are not tolerated on your team.", "correct": false },
      { "id": "c", "text": "Tell them 'It's totally okay, everyone makes mistakes!' but then silently hold a grudge against them during performance reviews.", "correct": false },
      { "id": "d", "text": "Thank them for their integrity in confessing immediately, shield them from executive fallout, and work together to implement systemic guardrails to prevent a recurrence.", "correct": true }
    ]
  }
];